import { useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { ModalV2, ButtonV2, FieldV2, TextareaV2, SelectV2 } from '@bloomays-lib/ui.shared';
import type { CancelSignatureReason } from '@bloomays-lib/types.shared';

const CANCEL_SIGNATURE_REASON_CATEGORIES: CancelSignatureReason[] = [
  'contractualizationAborted',
  'errorsInDocument',
  'other',
];

export type CancelLegalContractSignaturePayload = {
  reason?: string;
  reasonCategory?: CancelSignatureReason;
};

export type CancelLegalContractSignatureModalProps = {
  open: boolean;
  loading?: boolean;
  onClose: () => void;
  onConfirm: (payload: CancelLegalContractSignaturePayload) => void | Promise<void>;
};

const CancelLegalContractSignatureModal = ({
  open,
  loading = false,
  onClose,
  onConfirm,
}: CancelLegalContractSignatureModalProps) => {
  const { t } = useTranslation(['legalContracts']);
  const [reason, setReason] = useState('');
  const [reasonCategory, setReasonCategory] = useState<CancelSignatureReason | ''>('');

  useEffect(() => {
    if (!open) {
      setReason('');
      setReasonCategory('');
    }
  }, [open]);

  const handleConfirm = async () => {
    if (loading) return;
    await onConfirm({
      reason: reason.trim() || undefined,
      reasonCategory: reasonCategory || undefined,
    });
  };

  return (
    <ModalV2
      open={open}
      onClose={onClose}
      width={520}
      iconTone="rouge"
      icon={<CancelOutlinedIcon sx={{ fontSize: 18 }} />}
      title={<ModalTitle>{t('cancelModalTitle')}</ModalTitle>}
      footer={
        <ModalFooter>
          <ButtonV2 variant="secondary" onClick={onClose}>
            {t('cancelModalKeep')}
          </ButtonV2>
          <ButtonV2
            variant="primary"
            loading={loading}
            startIcon={<CancelOutlinedIcon sx={{ fontSize: 16 }} />}
            onClick={handleConfirm}
          >
            {t('cancelModalConfirm')}
          </ButtonV2>
        </ModalFooter>
      }
    >
      <ModalWarning>{t('cancelModalWarning')}</ModalWarning>
      <FieldV2 label={t('cancelReasonCategoryLabel')} hint={t('cancelReasonCategoryHint')}>
        <SelectV2
          value={reasonCategory}
          onChange={(value) => setReasonCategory(value as CancelSignatureReason | '')}
          placeholder={t('cancelReasonCategoryPlaceholder')}
          ariaLabel={t('cancelReasonCategoryLabel')}
          options={CANCEL_SIGNATURE_REASON_CATEGORIES.map((category) => ({
            value: category,
            label: t(`cancelReasonCategory.${category}`),
          }))}
        />
      </FieldV2>
      <FieldV2 label={t('cancelReasonLabel')} hint={t('cancelReasonHint')}>
        <TextareaV2
          value={reason}
          onChange={setReason}
          rows={3}
          maxLength={500}
          placeholder={t('cancelReasonPlaceholder')}
          ariaLabel={t('cancelReasonLabel')}
        />
      </FieldV2>
    </ModalV2>
  );
};

export default CancelLegalContractSignatureModal;

const ModalTitle = styled('span')(({ theme }) => ({
  display: 'block',
  fontSize: 13,
  fontWeight: 600,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: theme.palette.neutral.black,
  lineHeight: 1.3,
}));

const ModalWarning = styled('p')(({ theme }) => ({
  margin: '0 0 14px',
  fontSize: 13,
  lineHeight: 1.5,
  color: theme.palette.neutral[80],
}));

const ModalFooter = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: 10,
  width: '100%',
}));
